// n=8 élite (PR-N8-3) — Vista del informe completado (web admin, lo que ve el
// entrenador). SIEMPRE visible, sin descarga (D6). Layout del doc §3:
//   §1 header jugador + métricas Vertex · §2 evolución intra-periodo ·
//   §3 media global + estado + RADAR (21) · §4 objetivos EV2 · §6 tabla 3 niveles.
// Componente PURO (todo por props). La evolución ENTRE informes es N8-4.

import EvaluationRadar from './EvaluationRadar';
import EvaluationEvolutionLines from './EvaluationEvolutionLines';
import FieldPitch from './FieldPitch';
import { AlertTriangle } from 'lucide-react';
import {
  scoreColor,
  STATUS_LABEL,
  versionesSoportadas,
  type EvaluationReportV2,
} from '../lib/evaluationReportV2';
import { fieldPositionLabel } from '../lib/fieldPositions';
import type { ObjectiveStatus } from '../lib/evaluationObjectives';

export interface ReportObjectiveReviewItem {
  pillarLabel: string;
  description: string;
  status: ObjectiveStatus;
  comment: string | null;
}
export interface ReportNextObjectiveItem {
  pillarLabel: string;
  description: string;
}
export interface ReportMetrics {
  /**
   * `null` es «no se ha podido contar», y NO es lo mismo que 0. Este número se
   * imprime en un informe que se le da a una familia: sin las inscripciones,
   * antes salía «0» —un dato sobre el chaval— en vez de «—».
   */
  sessionsAttended: number | null;
  attendancePct: number | null;
  totalEvaluations: number;
}

interface Props {
  reportNumber: string;
  student: { name: string; age: number | null; position: string | null; group: string | null };
  trainerName: string;
  period: { start: string; end: string | null };
  metrics: ReportMetrics;
  // null = el informe se puntuó con un catálogo que esta versión no sabe leer.
  // No se pinta el informe: rellenarlo con defaults daría notas inventadas.
  model: EvaluationReportV2 | null;
  // Versión guardada en la evaluación; solo se usa para carteles y diagnóstico.
  catalogVersion: number;
  overallComment: string | null;
  intraPeriod: { date: string; rating: number }[];
  objectivesReview: ReportObjectiveReviewItem[];
  nextObjectives: ReportNextObjectiveItem[];
  // PJ-3 — Posición de campo del informe (principal + secundaria), snapshot.
  fieldPosition?: { primary: string | null; secondary: string | null };
}

const OBJ_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  pending: 'Pendiente',
  achieved: 'Conseguido',
  partial: 'Parcial',
  not_achieved: 'No conseguido',
  maintained: 'Mantenido',
};
const OBJ_STATUS_CLASS: Record<ObjectiveStatus, string> = {
  pending: 'badge-silver',
  achieved: 'badge-success',
  partial: 'badge-warning',
  not_achieved: 'badge-error',
  maintained: 'badge-gold',
};

function fmt(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function EvaluationReportView(props: Props) {
  const {
    reportNumber,
    student,
    trainerName,
    period,
    metrics,
    model,
    catalogVersion,
    overallComment,
    intraPeriod,
    objectivesReview,
    nextObjectives,
    fieldPosition,
  } = props;

  // Catálogo no soportado: se identifica el informe y se dice por qué no se
  // puede mostrar. Antes esto salía como un informe completo de 5.0 "Completado".
  if (model == null) {
    return (
      <div className="space-y-6" data-testid="evaluation-report">
        <div className="card" data-testid="report-unsupported-catalog">
          <p className="label">Informe</p>
          <p className="font-mono text-xs text-gold-primary">{reportNumber}</p>
          <p className="font-oswald text-2xl text-white mt-1">{student.name}</p>
          <p className="font-inter text-text-secondary text-xs mt-1">
            Periodo: {fmt(period.start)} → {fmt(period.end)} · Entrenador: {trainerName}
          </p>
          <div className="mt-4 flex items-start gap-2 border-t border-bg-border pt-4">
            <AlertTriangle size={18} className="text-warning shrink-0 mt-0.5" />
            <div>
              <p className="font-oswald text-white">Informe de un catálogo no soportado</p>
              <p className="font-inter text-text-secondary text-sm mt-1">
                Se puntuó con el catálogo <span className="font-mono">v{catalogVersion}</span> y
                esta versión sabe leer{' '}
                <span className="font-mono">
                  {versionesSoportadas()
                    .map((v) => `v${v}`)
                    .join(', ')}
                </span>
                . No se muestran notas: las que se pintaran no serían las que puso el entrenador.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const posPrimary = fieldPosition?.primary ?? null;
  const posSecondary = fieldPosition?.secondary ?? null;
  const hasPosition = Boolean(posPrimary || posSecondary);
  const headerBits = [
    student.age != null ? `${student.age} años` : null,
    student.position,
    student.group,
    `Entrenador: ${trainerName}`,
  ].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="evaluation-report">
      {/* §1 — Header jugador + métricas Vertex */}
      <div className="card" data-testid="report-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="label">Informe</p>
            <p className="font-mono text-xs text-gold-primary">{reportNumber}</p>
            <p className="font-oswald text-2xl text-white mt-1">{student.name}</p>
            <p className="font-inter text-text-secondary text-sm">{headerBits.join(' · ')}</p>
            <p className="font-inter text-text-secondary text-xs mt-1">
              Periodo: {fmt(period.start)} → {fmt(period.end)}
            </p>
          </div>

          {/* PJ-3 — Campo con la posición del jugador (parte superior del informe) */}
          {hasPosition && (
            <div className="flex flex-col items-center" data-testid="report-field-position">
              <FieldPitch primary={posPrimary} secondary={posSecondary} width={132} />
              <div className="font-inter text-xs text-text-secondary mt-1 text-center space-y-0.5">
                {posPrimary && (
                  <p>
                    <span className="text-gold-primary">●</span> {fieldPositionLabel(posPrimary)}
                  </p>
                )}
                {posSecondary && (
                  <p>
                    <span style={{ color: '#FFF20099' }}>○</span> {fieldPositionLabel(posSecondary)}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid="report-metrics">
          <Metric
            label="Sesiones (periodo)"
            value={metrics.sessionsAttended != null ? String(metrics.sessionsAttended) : '—'}
          />
          <Metric
            label="Asistencia"
            value={metrics.attendancePct != null ? `${metrics.attendancePct}%` : '—'}
          />
          <Metric label="Nº evaluaciones" value={String(metrics.totalEvaluations)} />
        </div>
      </div>

      {/* §2 — Evolución intra-periodo (quick_notes). Oculto si 0 notas. */}
      {intraPeriod.length > 0 && (
        <div className="card" data-testid="report-intra-period">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
            Evolución del periodo (valoración por sesión)
          </h3>
          <EvaluationEvolutionLines
            points={intraPeriod.map((p) => ({ label: fmt(p.date) }))}
            series={[
              {
                key: 'rating',
                label: 'Valoración',
                color: '#FFF200',
                values: intraPeriod.map((p) => p.rating),
              },
            ]}
            yMin={1}
          />
        </div>
      )}

      {/* §3 — Media global + estado + RADAR (21 ejes) */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-6 mb-2">
          <div>
            <p className="label">Valoración global</p>
            <p
              className="font-oswald text-4xl"
              style={{ color: scoreColor(model.overall) }}
              data-testid="report-overall"
            >
              {model.overall.toFixed(1)}
            </p>
          </div>
          <div>
            <p className="label">Estado</p>
            <p className="font-oswald text-lg text-white">{STATUS_LABEL[model.status]}</p>
          </div>
          {/* Leyenda de pilares (color/orden del radar) */}
          <div className="flex flex-wrap gap-3 ml-auto">
            {model.pillars.map((p) => (
              <span key={p.key} className="flex items-center gap-1.5 text-xs text-text-secondary">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{ backgroundColor: p.color }}
                />
                {p.label}
              </span>
            ))}
          </div>
        </div>
        <EvaluationRadar axes={model.radar} />
        {overallComment && (
          <p
            className="font-inter text-sm text-text-secondary italic mt-2"
            data-testid="report-overall-comment"
          >
            {overallComment}
          </p>
        )}
      </div>

      {/* §4 — Objetivos individuales (EV2) */}
      {(objectivesReview.length > 0 || nextObjectives.length > 0) && (
        <div className="card" data-testid="report-objectives">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3">
            Objetivos individuales
          </h3>
          {objectivesReview.length > 0 && (
            <div className="mb-4">
              <p className="label mb-2">Revisión del periodo</p>
              <ul className="space-y-2">
                {objectivesReview.map((o, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`${OBJ_STATUS_CLASS[o.status]} shrink-0`}>
                        {OBJ_STATUS_LABEL[o.status]}
                      </span>
                      <span className="text-white">
                        [{o.pillarLabel}] {o.description}
                      </span>
                    </div>
                    {o.comment && (
                      <p className="text-text-secondary text-xs italic ml-1 mt-0.5">{o.comment}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {nextObjectives.length > 0 && (
            <div>
              <p className="label mb-2">Para el próximo periodo</p>
              <ul className="space-y-1 list-disc list-inside">
                {nextObjectives.map((o, i) => (
                  <li key={i} className="text-sm text-white">
                    <span className="badge-silver mr-1">{o.pillarLabel}</span>
                    {o.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* §6 — Tabla de resultados (3 niveles) */}
      <div className="space-y-4" data-testid="report-table">
        {model.pillars.map((p) => (
          <div key={p.key} className="card p-0 overflow-hidden">
            <div
              className="px-4 py-2 flex items-center justify-between"
              style={{ backgroundColor: `${p.color}22` }}
            >
              <span className="font-oswald text-white">{p.label}</span>
              <span className="font-oswald" style={{ color: scoreColor(p.score) }}>
                {p.score != null ? p.score.toFixed(1) : '—'}
              </span>
            </div>
            <div className="divide-y divide-bg-border">
              {p.categories.map((c) => (
                <div key={c.key} className="px-4 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-inter text-sm text-white">{c.label}</span>
                    <span
                      className="font-oswald text-sm"
                      style={{ color: scoreColor(c.score) }}
                      data-testid={`cat-${c.key}-score`}
                    >
                      {c.score != null ? c.score.toFixed(1) : '—'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                    {c.subs.map((s) => (
                      <span key={s.key} className="text-xs text-text-secondary">
                        {s.label}:{' '}
                        <span style={{ color: scoreColor(s.score) }}>
                          {s.score != null ? s.score : '—'}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {p.comment && (
              <p className="px-4 py-2 text-xs text-text-secondary italic border-t border-bg-border">
                {p.comment}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-bg-elevated rounded-lg px-3 py-2">
      <p className="label">{label}</p>
      <p className="font-oswald text-xl text-gold-primary">{value}</p>
    </div>
  );
}
